import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Sales.css'; // Custom CSS

const Sales = () => {
    const [salesRecords, setSalesRecords] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteSaleId, setDeleteSaleId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editSale, setEditSale] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [customers, setCustomers] = useState([]);
    const navigate = useNavigate();

    const token = sessionStorage.getItem('token');
    const user = JSON.parse(sessionStorage.getItem('user'));

    // Fetch all sellers for admin only
    useEffect(() => {
        if (user.role === 'admin') {
            const fetchSellers = async () => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
                        headers: { 'x-auth-token': token },
                    });
                    setSellers(response.data.filter(u => u.role === 'employee' || u.role === 'admin'));
                } catch (error) {
                    console.error('Error fetching sellers', error);
                }
            };
            fetchSellers();
        }
    }, [token, user.role]);

    // Fetch sales records
    useEffect(() => {
        const fetchSales = async () => {
            setLoading(true);
            try {
                const endpoint = user.role === 'admin' && selectedSeller
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/vehiclesales?seller=${selectedSeller}`
                    : `${process.env.REACT_APP_BACKEND_URL}/api/vehiclesales`;

                const response = await axios.get(endpoint, {
                    headers: { 'x-auth-token': token },
                });
                setSalesRecords(response.data);
                console.log(response.data);
            } catch (error) {
                console.error('Error fetching sales records', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSales();
    }, [token, selectedSeller, user.role]);

    useEffect(() => {
        document.body.classList.toggle('scroll-lock', showDeleteModal);
    }, [showDeleteModal]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    };

    const handleRowClick = (rowIndex) => {
        setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    };

    const handleDeleteClick = (saleId) => {
        setDeleteSaleId(saleId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/vehiclesales/${deleteSaleId}`, {
                headers: { 'x-auth-token': token },
            });
            setSalesRecords(salesRecords.filter(sale => sale._id !== deleteSaleId));
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting sale', error);
            setShowDeleteModal(false);
        }
    };

    const handleEditClick = async (sale) => {
        setEditSale({
            ...sale,
            seller: sale.seller && typeof sale.seller === 'object' ? sale.seller : { _id: sale.seller },
            customer: sale.customer && typeof sale.customer === 'object' ? sale.customer : { _id: sale.customer },
        });

        // Fetch all users (customers and sellers) if admin
        if (user.role === 'admin') {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
                    headers: { 'x-auth-token': token },
                });
                setCustomers(response.data.filter(user => user.role === 'customer'));
                setSellers(response.data.filter(user => user.role === 'employee' || user.role === 'admin'));
            } catch (error) {
                console.error('Error fetching users', error);
            }
        }

        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;

        setEditSale((prevSale) => {
            if (name === 'seller' || name === 'customer') {
                return { ...prevSale, [name]: value };
            } else {
                const keys = name.split('.');
                let updatedSale = { ...prevSale };
                let ref = updatedSale;

                // Navigate through the object structure to set the value
                for (let i = 0; i < keys.length - 1; i++) {
                    ref = ref[keys[i]];
                }

                ref[keys[keys.length - 1]] = value;
                return updatedSale;
            }
        });
    };

    const saveEdit = async (e) => {
        e.preventDefault();

        // Ensure customer and seller contain only their _id when sending to backend
        const updatedSale = {
            ...editSale,
            seller: user.role === 'admin' ? editSale.seller._id || editSale.seller : salesRecords.find(sale => sale._id === editSale._id)?.seller._id,
            customer: user.role === 'admin' ? editSale.customer._id || editSale.customer : salesRecords.find(sale => sale._id === editSale._id)?.customer._id,
        };

        console.log("Sending data:", updatedSale);

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/vehiclesales/${editSale._id}`,
                updatedSale,
                {
                    headers: { 'x-auth-token': token },
                }
            );

            // Find seller and customer details locally for display update
            const updatedSeller = sellers.find((seller) => seller._id === updatedSale.seller);
            const updatedCustomer = customers.find((customer) => customer._id === updatedSale.customer);

            setSalesRecords((prevSales) =>
                prevSales.map((sale) =>
                    sale._id === editSale._id
                        ? {
                            ...editSale,
                            seller: updatedSeller || sale.seller,
                            customer: updatedCustomer || sale.customer,
                        }
                        : sale
                )
            );

            setShowEditModal(false);
        } catch (error) {
            console.error('Error updating sale', error.response?.data || error.message);
        }
    };


    return (
        <div className={`sales ${showDeleteModal || showEditModal ? 'blur-background' : ''}`}>
            <div className="sales_top">
                <div className="sales_heading">SALE RECORDS</div>
                <div className="sales_controls">
                    {user.role === 'admin' && (
                        <>
                            <select
                                className="seller-filter"
                                value={selectedSeller}
                                onChange={(e) => setSelectedSeller(e.target.value)}
                            >
                                <option value="">All Sellers</option>
                                {sellers.map((seller) => (
                                    <option key={seller._id} value={seller._id}>
                                        {seller.name}
                                    </option>
                                ))}
                            </select>

                        </>
                    )}
                    <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard/create-sale')}>
                        Create New Sale
                    </button>
                </div>
            </div>

            {loading && <div className="loading">Loading...</div>}

            {!loading && (
                <table className="table">
                    <thead>
                        <tr>
                            <th>Sale Date</th>
                            <th>Customer</th>
                            {user.role === 'admin' ? <th>Seller</th> : <th>Customer Email</th>}
                            <th>Vehicle</th>
                            <th>Vehicle Price</th>
                            <th>Amount Paid</th>
                            <th>Amount Due</th>
                            <th>Estimated Delivery</th>
                            <th>Payment Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {salesRecords.map((sale, index) => (
                            <React.Fragment key={sale._id}>
                                <tr onClick={() => handleRowClick(index)} style={{ cursor: 'pointer' }}>
                                    <td>{formatDate(sale.saleDate)}</td>
                                    <td>{sale.customer?.name || 'N/A'}</td>
                                    {user.role === 'admin' ? (
                                        <td>{sale.seller?.name || 'N/A'}</td>
                                    ) : (
                                        <td>{sale.customer?.email || 'N/A'}</td>
                                    )}
                                    <td>{sale.vehicleDetails.make} {sale.vehicleDetails.model} ({sale.vehicleDetails.year})</td>
                                    <td>{sale.paymentDetails.currency} {sale.vehicleDetails.price}</td>
                                    <td>{sale.paymentDetails.currency} {sale.paymentDetails.amountPaid}</td>
                                    <td>{sale.paymentDetails.currency} {sale.paymentDetails.amountDue}</td>
                                    <td>{formatDate(sale.estimatedDelivery)}</td>
                                    <td>{sale.paymentDetails.paymentStatus}</td>
                                    <td>
                                        <button className="btn btn-warning" onClick={() => handleEditClick(sale)}>Edit</button>
                                        {user.role === 'admin' && (
                                            <button className="btn btn-danger" onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(sale._id);
                                            }}>Delete</button>
                                        )}
                                    </td>
                                </tr>

                                {expandedRow === index && (
                                    <tr className="expanded-row">
                                        <td colSpan="10">
                                            <div className="expanded-details">
                                                <div className="expanded-details-heading">Sale Details</div>
                                                <div className="expanded-content">
                                                    <div>
                                                        <p><strong>Customer Name:</strong> {sale.customer?.name || 'N/A'}</p>
                                                        <p><strong>Customer Email:</strong> {sale.customer?.email || 'N/A'}</p>
                                                        <p><strong>Customer Phone:</strong> {sale.customer?.phone || 'N/A'}</p>
                                                    </div>
                                                    {user.role === 'admin' && (
                                                        <div>
                                                            <p><strong>Seller Name:</strong> {sale.seller?.name || 'N/A'}</p>
                                                            <p><strong>Seller Email:</strong> {sale.seller?.email || 'N/A'}</p>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p><strong>VIN:</strong> {sale.vehicleDetails.vin}</p>
                                                        <p><strong>Vehicle Make:</strong> {sale.vehicleDetails.make}</p>
                                                        <p><strong>Vehicle Model:</strong> {sale.vehicleDetails.model}</p>
                                                        <p><strong>Vehicle Year:</strong> {sale.vehicleDetails.year}</p>
                                                    </div>
                                                    <div>
                                                        <p><strong>Total Price:</strong> {sale.paymentDetails.currency} {sale.vehicleDetails.price}</p>
                                                        <p><strong>Payment Due:</strong> {sale.paymentDetails.currency} {sale.paymentDetails.amountDue}</p>
                                                        <p><strong>Payment Made:</strong> {sale.paymentDetails.currency} {sale.paymentDetails.amountPaid}</p>
                                                        <p><strong>Payment Status:</strong> {sale.paymentDetails.paymentStatus}</p>
                                                    </div>
                                                    <div>
                                                        <p><strong>Status:</strong> {sale.status}</p>
                                                        <p><strong>Sale Date:</strong> {formatDate(sale.saleDate)}</p>
                                                        <p><strong>Estimated Delivery Date:</strong> {formatDate(sale.estimatedDelivery)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    {/* Modal Background Blur */}
                    <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className='modal-heading'>DELETE</div>
                            <h3>Are you sure you want to delete this sale?</h3>
                            <div className="modal-actions">
                                <button className="btn modal-actions-btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                                <button className="btn modal-actions-btn-danger" onClick={confirmDelete}>Delete</button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {showEditModal && editSale && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className='modal-heading'>Edit Sale</div>
                        <form onSubmit={saveEdit}>
                            {user.role === 'admin' && (
                                <>
                                    <div className='edit-form-group'>
                                        <label>Seller</label>
                                        <select
                                            name="seller"
                                            value={editSale.seller._id || editSale.seller || ''}
                                            onChange={handleEditChange}
                                        >
                                            <option value="">Select Seller</option>
                                            {sellers.map((seller) => (
                                                <option key={seller._id} value={seller._id}>
                                                    {seller.name} ({seller.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className='edit-form-group'>
                                        <label>Customer</label>
                                        <select
                                            name="customer"
                                            value={editSale.customer._id || editSale.customer || ''}
                                            onChange={handleEditChange}
                                        >
                                            <option value="">Select Customer</option>
                                            {customers.map((customer) => (
                                                <option key={customer._id} value={customer._id}>
                                                    {customer.name} ({customer.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className='edit-form-group'>
                                <label>VIN</label>
                                <input
                                    type="text"
                                    name="vehicleDetails.vin"
                                    value={editSale.vehicleDetails.vin}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Vehicle Make</label>
                                <input
                                    type="text"
                                    name="vehicleDetails.make"
                                    value={editSale.vehicleDetails.make}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Vehicle Model</label>
                                <input
                                    type="text"
                                    name="vehicleDetails.model"
                                    value={editSale.vehicleDetails.model}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Vehicle Year</label>
                                <input
                                    type="number"
                                    name="vehicleDetails.year"
                                    value={editSale.vehicleDetails.year}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Total Price</label>
                                <input
                                    type="number"
                                    name="vehicleDetails.price"
                                    value={editSale.vehicleDetails.price}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Amount Paid</label>
                                <input
                                    type="number"
                                    name="paymentDetails.amountPaid"
                                    value={editSale.paymentDetails.amountPaid}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Amount Due</label>
                                <input
                                    type="number"
                                    name="paymentDetails.amountDue"
                                    value={editSale.paymentDetails.amountDue}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Sale Date</label>
                                <input
                                    type="date"
                                    name="saleDate"
                                    value={editSale.saleDate.split('T')[0]}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Est. Delivery Date</label>
                                <input
                                    type="date"
                                    name="estimatedDelivery"
                                    value={editSale.estimatedDelivery.split('T')[0]}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className='edit-form-group'>
                                <label>Shipping Status</label>
                                <select
                                    name="status"
                                    value={editSale.status}
                                    onChange={handleEditChange}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="in progress">In Progress</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className='edit-form-group'>
                                <label>Payment Status</label>
                                <select
                                    name="paymentDetails.paymentStatus"
                                    value={editSale.paymentDetails.paymentStatus}
                                    onChange={handleEditChange}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Paid">Paid</option>
                                </select>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn modal-actions-btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="submit" className="btn modal-actions-btn-danger">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sales;
